import { segment } from '@actions/analytics';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import useGeoLocation from '@api/hooks/useGeoLocation';
import constants, {
  EXPERIMENT_LOGIN_OTP_SCREEN_VIEWED,
  SIGNUP_FLOW_OPTIMIZATIONS
} from '@cont/Login/constants';
import useLoginStore from '@stores/login/loginStore';
import { getOTPSender, getPhoneVerifier } from '@stores/selectors/login';
import { abProxyJWT, abVariations } from '@utils/abHelper';
import { useCallback } from 'react';

const useSubmitNumber = ({
  step,
  setStep,
  mobileNumber,
  country,
  setNumberStepErrorMsg,
  sourceType,
  logOTPRequestedEvent
}) => {
  const { geoLocationData: { countryCode } = {} } = useGeoLocation();

  const sendOtpOrEmail = useLoginStore(getOTPSender);
  const verifyPhone = useLoginStore(getPhoneVerifier);

  return useCallback(async () => {
    if (step === constants.ADD_NUMBER_STEP) {
      verifyPhone({
        phone: mobileNumber,
        countryCode: country?.code || countryCode,
        otpType: 1
      }).then((response) => {
        if (response?.success_code) setStep(constants.VERIFY_OTP_STEP);
      });
    } else {
      const proxyToken = `${country?.code}${mobileNumber}`;
      const [experimentsResult, verifyUserResult] = await Promise.all([
        fetchABExperimentData(
          SIGNUP_FLOW_OPTIMIZATIONS,
          abProxyJWT,
          proxyToken
        ),

        sendOtpOrEmail({
          phone: mobileNumber,
          countryCode: country?.code || countryCode,
          otpType: 1
        })
      ]);

      if (experimentsResult?.variation) {
        segment.track(EXPERIMENT_LOGIN_OTP_SCREEN_VIEWED, {
          bucket: experimentsResult.variation
        });
      }

      if (verifyUserResult) {
        const { is_partial_user, is_registered, error, has_email, has_name } =
          verifyUserResult;
        if (error) {
          setNumberStepErrorMsg(error.message);
          return;
        }
        if (!is_registered && country.ageGroups?.length > 0) {
          setStep(constants.CONFIRM_AGE);
        } else if (
          !has_email &&
          !has_name &&
          ((experimentsResult?.variation === abVariations.TEST_1 &&
            !is_partial_user &&
            !is_registered) ||
            (is_partial_user && is_registered))
        ) {
          // not registered user or partially registered user
          setStep(constants.ENTER_OTP_UNREGISTERED_USER_STEP);
        } else {
          // registered user
          setStep(constants.ENTER_OTP_STEP);
        }
        if (!sourceType) logOTPRequestedEvent(verifyUserResult);
      }
    }
  }, [
    country?.ageGroups?.length,
    country?.code,
    countryCode,
    logOTPRequestedEvent,
    mobileNumber,
    setNumberStepErrorMsg,
    setStep,
    sourceType,
    step,
    sendOtpOrEmail,
    verifyPhone
  ]);
};

export default useSubmitNumber;
